import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CouponFormType } from 'app/main/design-system/models/coupon.type';
import {
  CampaignFiltersDTO,
  CommunicationCampaignOutDTO,
  Link,
  NotificationType,
} from '../../communicationCampaign/campaign.type';
import { MarketingType, MarketingWithRevenueDTO } from '../types';
import { DEFAULT_SEND_BEFORE } from '../utils/sendBefore.utils';

import marketingAdapter from './marketing.adapter';

export interface CurrentMarketing {
  communicationCampaign: CommunicationCampaignOutDTO;
  type: MarketingType;
  id: string;
  isNew: boolean;
  title: string;
  isActive: boolean;
  coupon?: CouponFormType;
  message: string;
  communicationCampaignAverageBasket?: number;
  communicationCampaignType: NotificationType;
  points?: number;
  sendBefore: string;
}

export interface MarketingState {
  ids: string[];
  entities: { [key: string]: MarketingWithRevenueDTO };
  current: CurrentMarketing;
  isLoading: boolean;
  error: boolean;
}

export const initialState: MarketingState = {
  ids: [],
  entities: {},
  current: {
    communicationCampaign: {
      type: NotificationType.notification,
      id: '',
      name: '',
      userId: '',
      campaignFilters: {
        storeIds: [],
        minAge: 0,
        maxAge: 0,
        zipOrPostcodes: [],
        clientsAcquiredFrom: [],
        radius: null,
        originProviderId: null,
        clientsTypes: undefined,
      },
      dateCreated: '',
      notification: {
        title: '',
        message: '',
        link: undefined,
      },
    },
    id: '',
    isNew: true,
    title: '',
    isActive: false,
    coupon: undefined,
    message: '',
    communicationCampaignAverageBasket: undefined,
    communicationCampaignType: NotificationType.notification,
    type: MarketingType.BIRTHDAY,
    sendBefore: DEFAULT_SEND_BEFORE,
  },
  isLoading: false,
  error: false,
};

export const { actions, reducer } = createSlice({
  name: 'automatedMarketing',
  initialState: marketingAdapter.getInitialState(initialState),
  reducers: {
    getAutomatedMarketingsSuccess: (state, action) => {
      marketingAdapter.setAll(state, action.payload);
    },
    getAutomatedMarketingsFailed: state => {
      marketingAdapter.setAll(state, []);
      state.error = true;
    },
    getTotalMarketingRevenueSuccess: (state, action) => {
      const marketing = state.entities[action.payload.automatedMarketingId];
      if (!marketing) return;
      marketing.totalMarketingRevenue = action.payload;
    },
    getTotalMarketingRevenueFailed: (state, action) => {
      const marketing = state.entities[action.payload.automatedMarketingId];
      if (!marketing) return;
      marketing.totalMarketingRevenue = undefined;
    },
    updateCurrentMarketing: (state, action: PayloadAction<Partial<CurrentMarketing>>) => {
      state.current = { ...state.current, ...action.payload };
    },
    updateCurrentMarketingCoupon: (state, action: PayloadAction<CouponFormType | undefined>) => {
      state.current.coupon = action.payload;
    },
    updateCurrentMarketingLink: (state, action: PayloadAction<Link | undefined>) => {
      state.current.communicationCampaign.notification.link = action.payload;
    },
    updateCurrentMarketingCampaignFilters: (state, action: PayloadAction<Partial<CampaignFiltersDTO>>) => {
      state.current.communicationCampaign.campaignFilters = {
        ...state.current.communicationCampaign.campaignFilters,
        ...action.payload,
      };
    },
    resetCurrentMarketing: (state: MarketingState, action: PayloadAction<MarketingType | undefined>) => {
      state.current = {
        ...initialState.current,
        type: action.payload || MarketingType.BIRTHDAY,
      };
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setTotalMarketingRevenueLoading: (state, action) => {
      const marketing = state.entities[action.payload.automatedMarketingId];
      if (!marketing) return;
      marketing.isTotalRevenueLoading = action.payload.isLoading;
    },
    setTotalMarketingRevenueError: (state, action) => {
      const marketing = state.entities[action.payload.automatedMarketingId];
      if (!marketing) return;
      marketing.isTotalRevenueError = action.payload.isError;
    },
  },
});

export const {
  getAutomatedMarketingsSuccess,
  getAutomatedMarketingsFailed,
  getTotalMarketingRevenueSuccess,
  getTotalMarketingRevenueFailed,
  updateCurrentMarketing,
  updateCurrentMarketingCoupon,
  updateCurrentMarketingLink,
  updateCurrentMarketingCampaignFilters,
  resetCurrentMarketing,
  setLoading,
  setError,
  setTotalMarketingRevenueLoading,
  setTotalMarketingRevenueError,
} = actions;

export default reducer;
